import { useState, useMemo, useRef, useEffect } from 'react'
import { useActionDispatch } from '../../hooks/useActionDispatch'
import { loadCyclopedia, allItems } from '../../lib/cyclopedia'
import type { ShopItem } from '../../lib/cyclopedia'

interface EquipmentItem {
  id: string
  item_name: string
}

interface Props {
  characterId: string
  campaignId: string
  containers: EquipmentItem[]
  onClose: () => void
}

const LOCATIONS = ['worn', 'backpack', 'saddlebag', 'cached', 'vehicle']

export function EquipmentShopDialog({ characterId, campaignId, containers, onClose }: Props) {
  const { dispatch } = useActionDispatch(characterId, campaignId)
  const establishments = useMemo(() => loadCyclopedia(), [])

  const [selectedEst, setSelectedEst] = useState<string>(establishments[0]?.key ?? '')
  const [search, setSearch] = useState('')
  const [selectedItem, setSelectedItem] = useState<ShopItem | null>(null)
  const [qty, setQty] = useState(1)
  const [location, setLocation] = useState('backpack')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => { searchRef.current?.focus() }, [])

  // Close on backdrop click
  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const isSearching = search.trim().length > 0

  const displayedItems = useMemo<ShopItem[]>(() => {
    if (isSearching) {
      const q = search.toLowerCase()
      return allItems().filter(i => i.name.toLowerCase().includes(q))
    }
    return establishments.find(e => e.key === selectedEst)?.items ?? []
  }, [isSearching, search, selectedEst, establishments])

  // Group by subsection for the current view
  const grouped = useMemo<{ subsection: string | null; items: ShopItem[] }[]>(() => {
    if (isSearching) {
      // Group by establishment when searching
      const byEst = new Map<string, ShopItem[]>()
      for (const item of displayedItems) {
        const list = byEst.get(item.establishment) ?? []
        list.push(item)
        byEst.set(item.establishment, list)
      }
      return Array.from(byEst.entries()).map(([est, items]) => ({
        subsection: establishments.find(e => e.key === est)?.label ?? est,
        items,
      }))
    }
    const bySection = new Map<string | null, ShopItem[]>()
    for (const item of displayedItems) {
      const list = bySection.get(item.subsection) ?? []
      list.push(item)
      bySection.set(item.subsection, list)
    }
    // null (top-level) first, then named subsections
    const result: { subsection: string | null; items: ShopItem[] }[] = []
    if (bySection.has(null)) result.push({ subsection: null, items: bySection.get(null)! })
    for (const [sub, items] of bySection.entries()) {
      if (sub !== null) result.push({ subsection: sub, items })
    }
    return result
  }, [displayedItems, isSearching, establishments])

  const acquire = async () => {
    if (!selectedItem) return
    setBusy(true); setError('')
    try {
      const isContainer = location.startsWith('container:')
      const containerId = isContainer ? location.slice('container:'.length) : undefined
      await dispatch('ACQUIRE_EQUIPMENT', {
        item_name: selectedItem.name,
        quantity: qty,
        weight_lbs: selectedItem.weight_lbs ?? undefined,
        value_denarii: selectedItem.value_denarii ?? undefined,
        location: isContainer ? undefined : location,
        parent_item_id: containerId,
      })
      setSelectedItem(null)
      setQty(1)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to acquire item')
    }
    setBusy(false)
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={handleBackdrop}
    >
      <div className="bg-white rounded-lg shadow-xl flex flex-col w-full max-w-5xl h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-parchment-dark flex-shrink-0">
          <h2 className="font-semibold text-base">Shop — HârnMaster Price Cyclopedia</h2>
          <button onClick={onClose} className="text-ink-light hover:text-ink text-lg leading-none px-2">✕</button>
        </div>

        {/* Search bar */}
        <div className="px-4 py-2 border-b border-parchment-dark flex-shrink-0">
          <input
            ref={searchRef}
            value={search}
            onChange={e => { setSearch(e.target.value); setSelectedItem(null) }}
            placeholder="Search all items..."
            className="w-full px-3 py-1.5 border border-parchment-dark rounded text-sm"
          />
        </div>

        {/* Body: sidebar + items table */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          {!isSearching && (
            <div className="w-52 flex-shrink-0 border-r border-parchment-dark overflow-y-auto bg-parchment/30">
              {establishments.map(est => (
                <button
                  key={est.key}
                  onClick={() => { setSelectedEst(est.key); setSelectedItem(null) }}
                  className={`w-full text-left px-3 py-2 border-b border-parchment/30 ${
                    selectedEst === est.key ? 'bg-parchment-dark font-semibold' : 'hover:bg-parchment/60'
                  }`}
                >
                  <div className="text-sm">{est.label}</div>
                  <div className="text-[10px] text-ink-light">{est.category}</div>
                </button>
              ))}
            </div>
          )}

          {/* Items table */}
          <div className="flex-1 overflow-y-auto">
            {displayedItems.length === 0 ? (
              <p className="text-ink-light text-sm p-4">
                {isSearching ? 'No items match.' : 'No items available.'}
              </p>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white border-b border-parchment-dark z-10">
                  <tr>
                    <th className="text-left px-3 py-2">Item</th>
                    <th className="text-right px-3 py-2 w-24">Weight</th>
                    <th className="text-right px-3 py-2 w-24">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped.map(({ subsection, items }) => (
                    <>
                      {subsection !== null && (
                        <tr key={`sub-${subsection}`}>
                          <td colSpan={3} className="px-3 py-1 bg-parchment/40 text-ink-light font-medium uppercase tracking-wide text-[10px]">
                            {subsection}
                          </td>
                        </tr>
                      )}
                      {items.map(item => (
                        <tr
                          key={item.key}
                          onClick={() => { setSelectedItem(item); setQty(1) }}
                          className={`border-b border-parchment/30 cursor-pointer ${
                            selectedItem?.key === item.key
                              ? 'bg-amber-50 border-amber-200'
                              : 'hover:bg-parchment/30'
                          }`}
                        >
                          <td className="px-3 py-1.5">
                            {item.name}
                            {item.is_service && <span className="text-ink-light ml-1 italic">(service)</span>}
                            {item.notes && (
                              <span className="text-ink-light ml-1 hidden sm:inline" title={item.notes}>
                                ℹ
                              </span>
                            )}
                          </td>
                          <td className="text-right px-3 py-1.5 text-ink-light">
                            {item.weight_lbs != null ? `${item.weight_lbs} lb` : '—'}
                          </td>
                          <td className="text-right px-3 py-1.5">
                            {item.value_display}
                          </td>
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Acquire bar */}
        <div className="flex-shrink-0 border-t border-parchment-dark px-4 py-3 bg-parchment/20">
          {error && <p className="text-xs text-danger mb-2">{error}</p>}
          {selectedItem ? (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-medium text-sm flex-1 min-w-0 truncate">{selectedItem.name}</span>
              {selectedItem.notes && (
                <span className="text-xs text-ink-light italic hidden md:block max-w-xs truncate" title={selectedItem.notes}>
                  {selectedItem.notes}
                </span>
              )}
              <div className="flex items-center gap-1">
                <label className="text-xs">Qty</label>
                <input
                  type="number" min={1} value={qty}
                  onChange={e => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-14 px-2 py-0.5 border border-parchment-dark rounded text-sm text-center"
                />
              </div>
              <div className="flex items-center gap-1">
                <label className="text-xs">To</label>
                <select
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  className="px-2 py-0.5 border border-parchment-dark rounded text-sm"
                >
                  {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
                  {containers.map(c => (
                    <option key={c.id} value={`container:${c.id}`}>In: {c.item_name}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={acquire}
                disabled={busy}
                className="px-3 py-1 text-sm bg-success text-white rounded hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
              >
                {busy ? 'Adding...' : '+ Acquire'}
              </button>
              <button
                onClick={() => setSelectedItem(null)}
                className="text-xs text-ink-light hover:text-ink"
              >
                Clear
              </button>
            </div>
          ) : (
            <p className="text-xs text-ink-light">Select an item to acquire it.</p>
          )}
        </div>
      </div>
    </div>
  )
}
